import React, { useCallback, useEffect, useMemo, useState } from "react";
import { currencySymbol, formatAmountForCopy, isSuffixCurrency } from "../utils/currency";
import { maxBoostFor, minPriceFor } from "../utils/payinConfig";
import NumberPad from "./NumberPad";
import "./BoostBidModal.css";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

// Bug C — Modal-driven bid boost. Every boost (1st through 3rd) opens this
// modal; the first boost arrives prefilled with 2x the current bid via
// `initialAmount`.
//
// Amount entry uses the app's own NumberPad (same keypad as the Requests
// price editor) instead of the iOS system keyboard — the display div below
// is read-only and driven entirely by pad keys.
//
// Validation kept dumb-simple: amount must be a positive number greater
// than the current bid and at most MAX_BOOST_AMOUNT. The same checks are
// re-run server-side at /payments/boost-intent — never trust client.
//
// FREE orders (currentAmount === 0): subline reads "Free request" and any
// amount above zero is accepted.

// Phase C: per-currency (mirror of server maxBoostFor). The component reads
// it from the order's currency prop below.
//
// Wallet pre-arm. While the fan types, the parent quotes the
// fee-inclusive total and arms an Apple Pay / Google Pay sheet so the orange
// button can open it directly (Safari only allows that synchronously inside
// the tap). `onQuote` reports the amount after a typing pause (null = no
// valid amount / modal closed → disarm); `walletMarkAmount` is the amount the
// parent has an APPLE PAY sheet armed for — when it matches what's typed, the
// button carries a small  Pay mark (Apple guideline: no unmarked buttons
// that open the sheet).

interface Props {
  isOpen: boolean;
  currentAmount: number;
  currency?: string | null;
  /** The DJ's cap tier (fraud door): admin-lifted accounts get the
      roomier boost ceiling; absent/false = the tight default. */
  capsLifted?: boolean | null;
  /** Prefill for the amount display (first boost passes 2x current bid). */
  initialAmount?: number | null;
  onClose: () => void;
  onSubmit: (newAmount: number) => Promise<void> | void;
  /** Debounced valid-amount reports for wallet pre-arming (null = disarm). */
  onQuote?: (amount: number | null) => void;
  /** Amount an Apple Pay sheet is armed for — shows the  Pay button mark. */
  walletMarkAmount?: number | null;
}

const BoostBidModal: React.FC<Props> = ({
  isOpen,
  currentAmount,
  currency,
  capsLifted = false,
  initialAmount = null,
  onClose,
  onSubmit,
  onQuote,
  walletMarkAmount = null,
}) => {
  const [input, setInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [padOpen, setPadOpen] = useState(true);
  // True while the prefill sits untouched — the first digit typed REPLACES
  // it (pad keys append, and nobody wants to backspace "40.00" first).
  const [pristine, setPristine] = useState(false);

  const sym = currencySymbol(currency);
  // "40 kr", not "kr40" — the eight suffix currencies write the symbol last.
  const suffixCur = isSuffixCurrency(currency);

  const maxBoost = maxBoostFor(currency, capsLifted === true);

  const minBoostFloor = minPriceFor(currency);
  const isFree = !(currentAmount > 0);

  // Fresh state on every open: apply the prefill (or blank) and bring the
  // keypad up so the fan can type immediately.
  useEffect(() => {
    if (!isOpen) return;
    const hasPrefill = typeof initialAmount === "number" && initialAmount > 0;
    setInput(hasPrefill ? initialAmount!.toFixed(2) : "");
    setPristine(hasPrefill);
    setPadOpen(true);
  }, [isOpen, initialAmount]);

  // While the prefill sits untouched the pad edits an EMPTY value (see the
  // NumberPad `value` prop below), so `next` here is already the fresh
  // amount the fan started typing — the prefill is replaced, not appended
  // to. (Appending used to trip the two-decimal cap on "40.00"+digit and
  // silently ate every key press.)
  const handlePad = (next: string) => {
    // Money: two decimal places max.
    const dot = next.indexOf(".");
    if (dot !== -1 && next.length - dot - 1 > 2) return;
    if (pristine) setPristine(false);
    setInput(next);
  };

  const parsed = useMemo(() => {
    if (input.trim() === "") return null;
    const n = Number(input);
    if (!Number.isFinite(n)) return NaN;
    return n;
  }, [input]);

  // Three error states, ordered by priority:
  //   1. user typed something non-numeric  → "must be a number"
  //   2. user typed a number ≤ current bid → "must be higher than your current Bid"
  //   3. user typed a number > the per-currency cap → "Maximum boost is …"
  // No error shown until the user has typed (so the hint state is clean).
  const errorMsg = useMemo<string | null>(() => {
    if (parsed === null) return null;
    if (Number.isNaN(parsed)) return tr("boostBidModal.newAmountMustBe", { sym });
    if (parsed <= currentAmount) {
      return isFree
        ? tr("boostBidModal.amountMustBeAbove")
        : tr("boostBidModal.newAmountMustBe2", { sym });
    }
    // Free orders have no DJ minimum — but Stripe has a per-currency charge
    // minimum, so a free-order boost still needs the currency floor
    // (mirrors the server's check in /payments/boost-intent).
    if (isFree && parsed < minBoostFloor) {
      return tr("boostBidModal.minimumBoostIs", { amount: formatAmountForCopy(minBoostFloor, currency) });
    }
    if (parsed > maxBoost) return tr("boostBidModal.maximumBoostIs", { amount: formatAmountForCopy(maxBoost, currency) });
    return null;
  }, [parsed, currentAmount, isFree, sym, maxBoost, minBoostFloor, currency]);

  // Shared validity: exactly the checks errorMsg enforces, used by canSubmit
  // AND the wallet quote below so the two can never disagree on what a
  // "boostable amount" is.
  const validAmount =
    parsed !== null &&
    !Number.isNaN(parsed) &&
    parsed > currentAmount &&
    (!isFree || parsed >= minBoostFloor) &&
    parsed <= maxBoost;

  const canSubmit = !submitting && validAmount;

  // Wallet pre-arm: report the amount once it has settled for a beat (the
  // quote + canMakePayment round-trip happens while the fan is still looking
  // at the number). Invalid/cleared input reports null so a stale armed sheet
  // can't outlive the amount it was priced for.
  useEffect(() => {
    if (!isOpen || !onQuote) return;
    const amount = validAmount ? parsed : null;
    const t = setTimeout(() => onQuote(amount), 450);
    return () => clearTimeout(t);
  }, [isOpen, onQuote, validAmount, parsed]);

  // Closing the modal (cancel, or the submit itself) disarms the parent —
  // the tap handler has already taken ownership of the armed sheet by the
  // time this cleanup runs, so a wallet submit is never disarmed mid-flight.
  useEffect(() => {
    if (!isOpen || !onQuote) return;
    return () => onQuote(null);
  }, [isOpen, onQuote]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || parsed === null || Number.isNaN(parsed)) return;
    setSubmitting(true);
    try {
      await onSubmit(parsed);
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, parsed, onSubmit]);

  // Desktop web: the pad is tap/mouse-first, but a physical keyboard must
  // keep working too (the old <input> allowed typing and Enter-to-submit).
  // Mirrors the pad's own rules: 7 chars max, one dot, two decimals.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (submitting) return;
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
        return;
      }
      const base = pristine ? "" : input;
      if (e.key === "Backspace") {
        e.preventDefault();
        setPristine(false);
        setInput(base.slice(0, -1));
        return;
      }
      if (e.key === "." || /^[0-9]$/.test(e.key)) {
        e.preventDefault();
        let next = base + e.key;
        if (e.key === ".") {
          if (base.includes(".")) return;
          if (base === "") next = "0.";
        }
        if (next.length > 7) return;
        const dot = next.indexOf(".");
        if (dot !== -1 && next.length - dot - 1 > 2) return;
        setPristine(false);
        setInput(next);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, submitting, pristine, input, handleSubmit]);

  if (!isOpen) return null;

  const hint = isFree
    ? tr("boostBidModal.enterAnyAmount")
    : tr("boostBidModal.enterAnyAmountAbove", { amount: formatAmountForCopy(currentAmount, currency) });

  return (
    <>
    <div
      className="boost-modal-overlay"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div className="boost-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="boost-modal-title">{tr("boostBidModal.boostYourBid")}</h3>
        <p className="boost-modal-sub">
          {isFree ? (
            tr("boostBidModal.freeRequest")
          ) : (
            <><T k="boostBidModal.currentBid" vars={{ amount: formatAmountForCopy(currentAmount, currency) }} tags={{ 0: (c) => <strong>{c}</strong> }} /></>
          )}
        </p>

        <div className="boost-modal-input-row">
          {!suffixCur && (
            <span className="boost-modal-currency-prefix" aria-hidden>{sym}</span>
          )}
          <div
            className={`boost-modal-input boost-modal-display${suffixCur ? " boost-modal-input--suffix" : ""} ${errorMsg ? "boost-modal-input-error" : ""}`}
            role="textbox"
            aria-readonly="true"
            aria-label={tr("boostBidModal.newBidAmount")}
            aria-invalid={!!errorMsg}
            onClick={() => {
              if (!submitting) setPadOpen(true);
            }}
          >
            {input === "" && !padOpen && (
              <span className="boost-modal-placeholder">
                {isFree ? "0.00" : currentAmount.toFixed(2)}
              </span>
            )}
            {input}
            {suffixCur && (
              <span className="boost-modal-currency-suffix" aria-hidden> {sym}</span>
            )}
            {padOpen && <span className="boost-modal-cursor" aria-hidden />}
          </div>
        </div>

        {/* Hint hides when an error is shown — error takes priority. */}
        {!errorMsg && (
          <p className="boost-modal-explainer">{hint}</p>
        )}
        {errorMsg && (
          <p className="boost-modal-error" role="alert">{errorMsg}</p>
        )}

        <div className="boost-modal-actions">
          <button
            type="button"
            className="boost-modal-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            {tr("boostBidModal.cancel")}
          </button>
          <button
            type="button"
            className="boost-modal-confirm"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? (
              tr("boostBidModal.boosting")
            ) : canSubmit && parsed !== null ? (
              <>
                {tr("boostBidModal.boostTo", { amount: formatAmountForCopy(parsed, currency) })}
                {walletMarkAmount !== null && walletMarkAmount === parsed && (
                  <span className="boost-modal-applepay-mark" aria-hidden>
                    <svg
                      width="10"
                      height="12"
                      viewBox="0 0 814 1000"
                      fill="currentColor"
                      focusable="false"
                    >
                      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.3 39.5-164.4 39.5-68.1 0-120.8-60.4-163.7-122.2C37.1 790.2 0 663.3 0 538.5 0 344.3 126.2 227.2 279.1 227.2c68.1 0 124.4 40.7 167.2 40.7 40.2 0 103.2-43.2 190.8-43.2 30.8 0 142.1 2.7 242.1 108.2zM585.1 152.8c35.3-41.9 61.1-99.8 61.1-158.4 0-8.2-.6-16.5-1.9-24.8-58.1 2.4-126.2 38.9-168.1 87.5-32.3 36.8-61.9 94.8-61.9 153.4 0 8.9.7 17.7 2.1 25.8 4.7.4 9.5.6 14.3.6 47.4 0 93.4-24.7 134.5-63.1z" />
                    </svg>
                    {tr("boostBidModal.pay")}
                  </span>
                )}
              </>
            ) : (
              tr("boostBidModal.boost")
            )}
          </button>
        </div>
      </div>
    </div>

    {/* Portaled to <body>; kept OUTSIDE the overlay so pad taps don't
        bubble (through the React tree) into the overlay's close handler. */}
    <NumberPad
      open={padOpen}
      // Pristine prefill = pad edits an empty value, so the first key press
      // starts a fresh amount (Done still submits the untouched prefill).
      value={pristine ? "" : input}
      onChange={handlePad}
      onDone={() => setPadOpen(false)}
      allowDecimal
      maxLength={7}
    />
    </>
  );
};

export default BoostBidModal;
